import React, { useState, lazy, Suspense, useRef, useEffect } from "react";
import { Flex, Text, Card, Button, Badge, IconButton, DropdownMenu, Separator, TextField, Select, Callout, AlertDialog, 
  Tooltip, Dialog, Box, Checkbox, Progress } from "@radix-ui/themes";
import { DotsHorizontalIcon, ChevronDownIcon, ChevronUpIcon, LockClosedIcon, InfoCircledIcon, GlobeIcon,
  ClockIcon, LapTimerIcon } from "@radix-ui/react-icons";
  import { MapPinIcon } from "@heroicons/react/24/outline";
import { useLocationContext, useProfileContext } from "context";
import { useFetchLocationType, useEditOrgLocation, useGetAllBrands, useCreateBrand, useUpdateCiscoMerakiSplashTimeout, useUpdateUnifiSplashTimeout, useUpdateTPLinkSplashTimeout } from "hooks";
import { useToast } from "../../context/ToastContext";
import { ToastAlert } from "components";
import { useTimezoneSelect, allTimezones } from "react-timezone-select";
import { useLocation, useNavigate } from "react-router-dom";

const sortByName = (items = []) => [...items].sort((firstItem, secondItem) =>
  (firstItem?.name || "").localeCompare(secondItem?.name || "", undefined, { sensitivity: "base" })
);

const CreateLocationModal = lazy(() => import("./CreateLocationModal"));
const EditBrandsModal = lazy(() => import("./EditBrandsModal"));

const TruncatedText = ({ children, size, weight, color, style }) => {
  const text = typeof children === 'string' ? children : String(children);
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const checkTruncation = () => {
      const container = containerRef.current;
      if (container) {
        setIsTruncated(container.scrollWidth > container.clientWidth);
      }
    };

    // Initial check
    checkTruncation();

    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);

    // Recheck if text changes
    const observer = new MutationObserver(checkTruncation);
    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true, characterData: true, subtree: true });
    }

    return () => {
      window.removeEventListener('resize', checkTruncation);
      observer.disconnect();
    };
  }, [text]); // Rerun effect if text content changes

  const textElement = (
    <Box as="span" ref={containerRef} style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...style // Allow passing additional styles
      }}>
      <Text size={size} weight={weight} color={color} style={{width: "100%"}}>
        {text}
      </Text>
    </Box>
  );

  return isTruncated ? (
    <Tooltip content={text}>
      {textElement}
    </Tooltip>
  ) : textElement;
};

const OrganisationLocations = () => {
  const { setToastMessage } = useToast();
  // Fetching all the locations info for the organisation
  const { locationsData, refetchLocations, isLocationsLoading } = useLocationContext();
  // Fetching all the location types
  const { data: locationTypes, isLoading: isLocationTypesLoading } = useFetchLocationType();
  const sortedLocationTypes = sortByName(locationTypes?.data || []);
  // Get user profile to check role
  const { userProfile } = useProfileContext();
  const isManager = userProfile?.user_role === 'Manager';
  const [isLoadingBrand, setIsLoadingBrand] = useState(false);

  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [editBrandsOpen, setEditBrandsOpen] = useState(false);
  useEffect(() => {
    if (routerLocation.state?.openEditBrands) {
      setEditBrandsOpen(true);
      navigate(routerLocation.pathname, { replace: true, state: {} });
    }
  }, [routerLocation.state, navigate, routerLocation.pathname]);

  // Sorting locations by name
  const sortedLocations = Array.isArray(locationsData) ? [...locationsData].sort((a, b) => (a?.name || "").toLowerCase().localeCompare((b?.name || "").toLowerCase())) : [];

  // Hook to update location details
  const updateLocation = useEditOrgLocation();
  const [errors, setErrors] = useState({});

  // Hook for updating splash timeout
  const updateSplashTimeoutMutation = useUpdateCiscoMerakiSplashTimeout();
  const updateUnifiSplashTimeoutMutation = useUpdateUnifiSplashTimeout();
  const updateTPLinkSplashTimeoutMutation = useUpdateTPLinkSplashTimeout();

  const [createBrandModalOpen, setCreateBrandModalOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [isCreatingBrand, setIsCreatingBrand] = useState(false);
  const [activeLocationId, setActiveLocationId] = useState(null);
  const createBrandMutation = useCreateBrand();

  const handleCreateBrand = async () => {
    if (!newBrandName.trim()) return;

    // Check if brand name already exists
    const brandExists = brandsData?.some((brand) => brand.name.toLowerCase() === newBrandName.toLowerCase().trim());

    if (brandExists) {
      setToastMessage("A brand with this name already exists");
      return;
    }

    try {
      setIsCreatingBrand(true);
      const response = await createBrandMutation.mutateAsync({
        orgID: sortedLocations?.[0]?.organisation,
        data: {
          name: newBrandName,
          location_ids: [],
        },
      });

      setToastMessage(`Brand "${newBrandName}" created successfully`);
      
      // Get the ID directly from the response instead of refetching
      if (activeLocationId && response.data.id) {
        handleFieldChange(activeLocationId, "brand_id", response.data.id);
      }

      // Reset state
      setNewBrandName("");

      // Close modal after a short delay to ensure state updates
      setTimeout(() => {
        setCreateBrandModalOpen(false);
        setActiveLocationId(null);
      }, 100);
    } catch (error) {
      console.error("Brand creation error:", error);
      setToastMessage(error.response?.data?.message || "Failed to create brand");
    } finally {
      setIsCreatingBrand(false);
    }
  };

  // Function to handle brand dropdown selection
  const handleBrandSelection = (locationId, value) => {
  // Managers cannot create brands or assign locations to brands
  if (isManager) {
    return;
  }
  
  if (value === "create_brand") {
    setActiveLocationId(locationId);
    setCreateBrandModalOpen(true);
  } else if (value === "_placeholder") {
    // If "None" is selected, remove brand_id from modifiedLocations
    // to prevent it from being treated as a change
    setModifiedLocations((prevLocations) => {
      const updated = { ...prevLocations };
      
      // If there's an entry for this location but only contains brand_id
      if (updated[locationId] && Object.keys(updated[locationId]).length === 1 && 'brand_id' in updated[locationId]) {
        delete updated[locationId]; // Remove the entire location entry
      } else if (updated[locationId]) {
        // If location has other modified fields, just remove brand_id
        const { brand_id, ...rest } = updated[locationId];
        updated[locationId] = rest;
      }
      
      return updated;
    });
  } else {
    // For actual brand selections
    handleFieldChange(locationId, "brand_id", parseInt(value));
  }
};

  // For the dropdown menu
  const [dropdownStates, setDropdownStates] = useState({});
  const handleShowDropdown = (locationId) => () => {
    setDropdownStates((prevStates) => ({
      ...prevStates,
      [locationId]: !prevStates[locationId],
    }));
  };

  const [modifiedLocations, setModifiedLocations] = useState({});

  const handleFieldChange = (locationId, field, value) => {
    if (errors[locationId]?.[field]) {
      setErrors({
        ...errors,
        [locationId]: {
          ...errors[locationId],
          [field]: undefined,
        },
      });
    }
    setModifiedLocations((prevLocations) => ({
      ...prevLocations,
      [locationId]: {
        ...prevLocations[locationId],
        [field]: value,
      },
    }));
  };

  // Fetch all brands for the organization
  const { data: brandsData, isLoading: isBrandsLoading } = useGetAllBrands(sortedLocations?.[0]?.organisation);

  // Filter out default brand
  const nonDefaultBrands = brandsData?.filter((brand) => !brand.is_default) || [];

  const handleSaveChanges = (locationId) => {
    const currentName = modifiedLocations[locationId]?.name ?? sortedLocations.find((loc) => loc.id === locationId).name;
    const currentAddress = modifiedLocations[locationId]?.address ?? sortedLocations.find((loc) => loc.id === locationId).address;

    // Validate fields
    const locationErrors = {};
    if (!currentName || currentName.trim() === "") {
      locationErrors.name = "Input location name.";
    }

    if (!currentAddress || currentAddress.trim() === "") {
      locationErrors.address = "Input location address.";
    }

    if (Object.keys(locationErrors).length > 0) {
      setErrors({ ...errors, [locationId]: locationErrors });
      return;
    }

    updateLocation.mutate(
      {
        id: locationId,
        name: modifiedLocations[locationId]?.name,
        address: modifiedLocations[locationId]?.address,
        location_type: sortedLocationTypes.find((type) => type.name === modifiedLocations[locationId]?.location_type)?.id,
        timezone: modifiedLocations[locationId]?.timezone,
      },
      {
        onSuccess: () => {
          setToastMessage("Changes saved");
          setModifiedLocations({});
          setErrors({});
          refetchLocations();
        },
      }
    );
  };

  // Actual function to save brand changes after confirmation
  const handleSaveBrandChanges = (locationId) => {
    setIsLoadingBrand(true);
    let brandIdToSend = modifiedLocations[locationId]?.brand_id;

    // If null/unassigned is selected, find the default brand
    if (brandIdToSend === null) {
      const defaultBrand = brandsData?.find((brand) => brand.is_default);
      if (defaultBrand) {
        brandIdToSend = defaultBrand.id;
      }
    }

    // Create a payload specifically for brand updates
    const payload = {
      id: locationId,
      brand_id: brandIdToSend,
    };

    // Make the PATCH request with just the brand_id
    updateLocation.mutate(payload, {
      onSuccess: () => {
        setToastMessage("Changes saved");
        setModifiedLocations((prev) => {
          const updated = { ...prev };
          delete updated[locationId];
          return updated;
        });
        refetchLocations();
      },
    });
    setIsLoadingBrand(false);
  };

  const locationsWithDefaultBrand = sortedLocations.filter((location) => {
    const currentBrand = brandsData?.find((b) => b.id === location.brand_id);
    return !currentBrand || currentBrand.is_default;
  }).length;

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  // Captive portal expiry confirmation checkbox state
  const [isExpiryCheckboxChecked, setIsExpiryCheckboxChecked] = useState(false);
  const [isSavingExpiry, setIsSavingExpiry] = useState(false);

  const labelStyle = "original";
  const timezones = {...allTimezones, "Europe/Berlin": "Frankfurt",};
  const { options: timezoneOptions, parseTimezone } = useTimezoneSelect({ labelStyle, timezones });

  // Getting user's timezone as fallback
  const getUserTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.error("Error getting user timezone:", error);
      return "UTC";
    }
  };

  // Helper function to check if location has supported network integrations
  const hasNetworkIntegration = (location) => {
    return location.integrations.some(integration => 
      ['cisco', 'unifi', 'tplink', 'cambium', 'extreme'].includes(integration.type) && integration.state === 'active'
    );
  };

  // Predefined timeout options with their values in minutes
  const timeoutOptions = [
    { label: "30 minutes", value: 30 },
    { label: "1 hour", value: 60 },
    { label: "2 hours", value: 120 },
    { label: "4 hours", value: 240 },
    { label: "8 hours", value: 480 },
    { label: "12 hours", value: 720 },
    { label: "18 hours", value: 1080 },
    { label: "1 day", value: 1440 },
    { label: "2 days", value: 2880 },
    { label: "4 days", value: 5760 },
    { label: "5 days", value: 7200 },
    { label: "1 week", value: 10080 },
    { label: "2 weeks", value: 20160 },
    { label: "1 month", value: 43200 },
    { label: "2 months", value: 86400 },
    { label: "3 months", value: 129600 }
  ];

  // Handler for splash timeout selection (deferred save)
const handleSplashTimeoutSelect = (locationId, selectedValue) => {
  const timeoutMinutes = parseInt(selectedValue);
  handleFieldChange(locationId, "splash_timeout", timeoutMinutes);
};

// Confirm and save splash timeout changes
const handleSaveSplashTimeoutChanges = (locationId) => {
  const selectedTimeout = modifiedLocations[locationId]?.splash_timeout;
  if (selectedTimeout === undefined) return;

  const location = sortedLocations.find((loc) => loc.id === locationId);
  const ciscoIntegration = location?.integrations.find(
    (integration) => integration.type === 'cisco' && integration.state === 'active'
  );
  const unifiIntegration = location?.integrations.find(
    (integration) => integration.type === 'unifi' && integration.state === 'active'
  );
  const tplinkIntegration = location?.integrations.find(
    (integration) => integration.type === 'tplink' && integration.state === 'active'
  );

  const onSuccessCommon = () => {
    setToastMessage("Guest authentication expiry updated successfully");
    // Remove only the splash_timeout change for this location
    setModifiedLocations((prev) => {
      const updated = { ...prev };
      if (updated[locationId]) {
        const { splash_timeout, ...rest } = updated[locationId];
        if (Object.keys(rest).length > 0) {
          updated[locationId] = rest;
        } else {
          delete updated[locationId];
        }
      }
      return updated;
    });
    setIsExpiryCheckboxChecked(false);
    refetchLocations();
  };

  const onErrorCommon = (error) => {
    const errorMessage = error?.response?.data?.message || "Failed to update guest authentication expiry";
    setToastMessage(errorMessage);
  };

  setIsSavingExpiry(true);
  if (ciscoIntegration) {
    updateSplashTimeoutMutation.mutate(
      { location_id: locationId, splash_timeout: selectedTimeout },
      { onSuccess: onSuccessCommon, onError: onErrorCommon, onSettled: () => setIsSavingExpiry(false) }
    );
  } else if (unifiIntegration) {
    updateUnifiSplashTimeoutMutation.mutate(
      { location_id: locationId, splash_timeout: selectedTimeout },
      { onSuccess: onSuccessCommon, onError: onErrorCommon, onSettled: () => setIsSavingExpiry(false) }
    );
  } else if (tplinkIntegration) {
    updateTPLinkSplashTimeoutMutation.mutate(
      { location_id: locationId, splash_timeout: selectedTimeout },
      { onSuccess: onSuccessCommon, onError: onErrorCommon, onSettled: () => setIsSavingExpiry(false) }
    );
  } else {
    setToastMessage("No supported network integration found");
    setIsSavingExpiry(false);
  }
};

const getCurrentSplashTimeout = (location) => {
  const networkIntegration = location?.integrations.find(integration => 
    ['cisco', 'unifi', 'tplink'].includes(integration.type) && integration.state === 'active'
  );
  return networkIntegration?.splash_timeout || 129600; // Default to 3 months if not found
};

const usesPerGuestTPLinkTimeout = (location) => {
  const activeIntegrationTypes = location?.integrations
    .filter((integration) => integration.state === 'active')
    .map((integration) => integration.type) ?? [];

  return activeIntegrationTypes.includes('tplink') &&
    !activeIntegrationTypes.some((type) => ['cisco', 'unifi'].includes(type));
};

  if (isLocationsLoading || isLocationTypesLoading || isBrandsLoading) {
    return (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 1000
        }}>
          <Progress size="1" duration="1" />
        </div>
    );
  }
  
  return (
    <React.Fragment>
      <Flex direction="column" gap="5" height="100%" style={{ alignSelf: "stretch", flex: "1 0 0" }}>
        {/* Header with stats and action buttons */}
        <Flex justify="between" width="100%" style={{ alignItems: 'center'}}>
          
          <Flex gap="2" align="center">
            <Badge size="3" color="amber" variant="surface">
              {sortedLocations.length} {sortedLocations.length === 1 ? "location" : "locations"}
            </Badge>
            <Badge size="3" color="amber" variant="surface">
              {nonDefaultBrands.length} {nonDefaultBrands.length === 1 ? "brand" : "brands"}
            </Badge>
          </Flex>

          <Flex gap="4" align="center">
            <Suspense>
              <EditBrandsModal open={editBrandsOpen} onOpenChange={setEditBrandsOpen} />
            </Suspense>
            {!isManager && (
              <Suspense>
                <CreateLocationModal buttontype="ghost" />
              </Suspense>
            )}
          </Flex>
        </Flex>

        {/* Location list container - separate from header */}
        <Flex className="location-details-container" direction="column"gap="5"
          style={{ alignItems: "flex-start", flex: "1 0 0", alignSelf: "stretch", overflow: "scroll" }}>
          {sortedLocations.length === 0 && (
            <Flex direction="column"
              style={{ justifyContent: "center", alignItems: "center", alignSelf: "stretch", 
              border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-4)", height: "100%" }}>
              <Flex direction="column" gap="5" maxWidth="296px" style={{ alignItems: "center", width: "100%" }}>
                <Flex gap="2" direction="column" style={{ alignItems: "center", alignSelf: "stretch", justifyContent: "center" }}>
                  <Text size="4" weight="medium" align="center">
                    No locations
                  </Text>
                  <Text size="2" weight="regular" align="center" color="gray">
                    Create your first location to start activating your integrations.
                  </Text>
                </Flex>
                <Suspense>
                  <CreateLocationModal buttontype="soft" />
                </Suspense>
              </Flex>
            </Flex>
          )}
          {sortedLocations &&
            sortedLocations.map((location) => (
              <Card key={location.id}
                style={{ display: "flex", flexDirection: "column", flex: "0 0 auto", alignItems: "flex-start", 
                  alignSelf: "stretch", gap: "var(--space-4)", padding: "var(--space-4)" }}>
                {/* Locations Header Section */}
                <Flex gap="2"
                  style={{ alignSelf: "stretch", alignItems: "center", justifyContent: "space-between" }}>
                  {/* Location Name & Address */}
                  <Flex direction="column" style={{ alignSelf: "stretch", alignItems: "flex-start", width: "30%" }}>
                    <TruncatedText size="3" weight="medium">
                      {location.name}
                    </TruncatedText>
                    <TruncatedText size="1" weight="medium" color="gray" style={{ width: "100%" }}>
                      {location.address}
                    </TruncatedText>
                  </Flex>

                  {/* Information Badges */}
                  <Flex gap="2" style={{ alignSelf: "stretch", alignItems: "center", 
                    justifyContent: "space-between", width: "70%" }}>
                    {/* No of access points */}
                    <Flex style={{ width: "100%", justifyContent: "center" }}>
                      <Badge size="1" variant="soft" color="gray">
                        <TruncatedText size="1">
                          {`${location.access_point_mac_list.length} ${location.access_point_mac_list.length === 1 
                            ? "Wi-Fi access point" 
                            : "Wi-Fi access points"}`
                          }
                        </TruncatedText>
                      </Badge>
                    </Flex>
                    {/* No of integrations */}
                    <Flex style={{ width: "100%", justifyContent: "center" }}>
                      <Badge size="1" variant="soft" color="gray">
                        <TruncatedText size="1">
                          {`${location.integrations.length} ${location.integrations.length === 1 ? "integration" : "integrations"}`}
                        </TruncatedText>
                      </Badge>
                    </Flex>
                    {/* Location Type */}
                    <Flex style={{ width: "100%", justifyContent: "center" }}>
                      <TruncatedText size="1" color="gray" style={{textAlign: "center" }}>
                        {location.location_type_name}
                      </TruncatedText>
                    </Flex>
                    {/* Associate brand */}
                    <Flex style={{ width: "100%", justifyContent: "center" }}>
                      <TruncatedText size="1" color="gray" style={{textAlign: "center" }}>
                        {(() => {
                          const brand = brandsData?.find((b) => b.id === location.brand_id);
                          return brand?.is_default ? 'No brand' : brand?.name;
                        })()}
                      </TruncatedText>
                    </Flex>
                    {/* Possible Actions */}
                    <Flex p="1" gap="4" style={{ alignItems: "center" }}>
                      {/* Ellipsis Icon for Deleting Location */}
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                          <IconButton size="1" variant="ghost" color="gray">
                            <DotsHorizontalIcon width="16" height="16" />
                          </IconButton>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content>
                          <DropdownMenu.Item
                            color="red"
                            onClick={() => {
                              const supportEmail = "support@b-connect.co.uk";
                              const subject = "Delete Account Request";
                              const body = `Hello, I need help with deleting this location ${location.name}(id: ${location.id}). Please assist me with this process.`;
                              const mailtoLink = `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                              window.location.href = mailtoLink;
                            }}
                          >
                            Delete
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Root>

                      {/* Arrow Icon for Editing Location Details */}
                      <IconButton size="1" variant="ghost" color="gray" onClick={handleShowDropdown(location.id)}>
                        {dropdownStates[location.id] ? <ChevronUpIcon width="16" height="16" /> : <ChevronDownIcon width="16" height="16" />}
                      </IconButton>
                    </Flex>
                  </Flex>
                </Flex>

                {/* Location Details Section */}
                {dropdownStates[location.id] && (
                  <Flex direction="column" gap="4" style={{ alignSelf: "stretch", alignItems: "flex-start" }}>
                    <Separator size="4" orientation="horizontal" />

                    {/* Location Details */}
                    <Flex gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                      <Flex direction="column" gap="2" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "25%" }}>
                        <Flex gap="2" align="center" style={{alignSelf: 'stretch'}}>
                          <MapPinIcon width={14} height={14} />
                          <TruncatedText size="3" weight="medium">Location details</TruncatedText>
                        </Flex>
                        <Text size="1" weight="regular" color="gray">Update your location name, type, and address as needed.</Text>
                      </Flex>
                      {/* Name and Type Fields */}
                      <Flex direction="column" gap="4" style={{ alignItems: "flex-end", alignSelf: "stretch", width: "75%", flex: "1 0 0" }}>
                        <Flex direction="row" gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch", justifyContent: "center" }}>
                          {/* Name Field */}
                          <Flex direction="column" gap="1" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "100%" }}>
                            <Text size="2" weight="medium" style={{ width: "100%" }}>
                              Name
                            </Text>
                            <TextField.Root
                              size="2"
                              style={{ width: "100%" }}
                              value={modifiedLocations[location.id]?.name ?? location.name}
                              onChange={(e) => handleFieldChange(location.id, "name", e.target.value)}
                            />
                            {errors[location.id]?.name && (
                              <Text size="1" color="red">
                                {errors[location.id].name}
                              </Text>
                            )}
                          </Flex>

                          {/* Type Select Field */}
                          <Flex direction="column" gap="1" style={{ alignItems: "center", alignSelf: "stretch", width: "100%" }}>
                            <Text size="2" weight="medium" style={{ width: "100%" }}>
                              Type
                            </Text>
                            <Select.Root style={{ width: "100%" }} 
                              defaultValue={location.location_type_name} 
                              onValueChange={(value) => handleFieldChange(location.id, "location_type", value)}>
                              <Select.Trigger variant="surface" style={{ width: "100%" }} />
                              <Select.Content position="popper">
                                <Select.Group>
                                  {sortedLocationTypes.map((type, index) => (
                                    <Select.Item key={index} value={type.name}>
                                      {type.name}
                                    </Select.Item>
                                  ))}
                                </Select.Group>
                              </Select.Content>
                            </Select.Root>
                          </Flex>
                        </Flex>

                        {/* Address Field */}
                        <Flex direction="column" gap="1" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Text size="2" weight="medium" style={{ width: "100%" }}>
                            Address
                          </Text>
                          <TextField.Root
                            size="2"
                            style={{ width: "100%" }}
                            value={modifiedLocations[location.id]?.address ?? location.address}
                            onChange={(e) => handleFieldChange(location.id, "address", e.target.value)}
                          />
                          {errors[location.id]?.address && (
                            <Text size="1" color="red">
                              {errors[location.id].address}
                            </Text>
                          )}
                        </Flex>

                        {/* Save Changes Button */}
                        <Button
                          size="2"
                          variant="solid"
                          style={{ cursor: modifiedLocations[location.id] ? "default" : "not-allowed" }}
                          disabled={
                            !modifiedLocations[location.id] ||
                            ((modifiedLocations[location.id]?.name ?? location.name) === location.name &&
                              (modifiedLocations[location.id]?.location_type ?? location.location_type) === location.location_type &&
                              (modifiedLocations[location.id]?.address ?? location.address) === location.address)
                          }
                          onClick={() => handleSaveChanges(location.id)}
                        >
                          Save changes
                        </Button>
                      </Flex>
                    </Flex>
                    <Separator size="4" orientation="horizontal" />

                    {/* Opening & Closing Times Section (read-only, merged overnight ranges) */}
                    <Flex gap="4" style={{ alignSelf: "stretch", alignItems: "flex-start" }}>
                      {/* Left column title: 40% on md+, full width on small */}
                      <Flex direction="column" gap="2" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "25%" }}>
                        <Flex gap="2" align="center" style={{alignSelf: 'stretch'}}>
                          <ClockIcon width={14} height={14} />
                          <TruncatedText size="3" weight="medium">Opening &amp; closing hours</TruncatedText>
                        </Flex>
                        <Text size="1" color="gray">Business hours powered by Google.</Text>
                      </Flex>

                      {/* Table area: 60% on md+, full width on small; fills remaining space */}
                      <Flex direction="column" gap="4" style={{ alignItems: "flex-end", alignSelf: "stretch", 
                        width: "75%", flex: "1 0 0" }}>
                        {(() => {
                          const days = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
                          const openingsByDay = (location.opening_times || Array(7).fill([])).map(arr => Array.isArray(arr) ? [...arr] : []);
                          const closingsByDay = (location.closing_times || Array(7).fill([])).map(arr => Array.isArray(arr) ? [...arr] : []);

                          const formatTime = (t) => {
                            if (!t) return "";
                            const [hh, mm] = t.split(":").map(Number);
                            const d = new Date();
                            d.setHours(hh === 24 ? 0 : hh, mm);
                            return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
                          };

                          // Prepare merged display data
                          const merged = Array(7).fill(null).map(() => []);

                          // Make local mutable copies we can splice/shift safely
                          const opensCopy = openingsByDay.map(a => [...a]);
                          const closesCopy = closingsByDay.map(a => [...a]);

                          // First pass: Mark ranges that are continuations from the previous day
                          // These should not be displayed as they're already merged with the previous day
                          const skipRanges = Array(7).fill(null).map(() => new Set());

                          for (let day = 0; day < 7; day++) {
                            const opens = opensCopy[day] || [];
                            const closes = closesCopy[day] || [];

                            for (let i = 0; i < opens.length; i++) {
                              const open = opens[i];
                              const close = closes[i];
                              if (!open || !close) continue;

                              // If this range ends at midnight (24:00), check if next day starts at 00:00
                              if (close === "24:00") {
                                const nextDay = (day + 1) % 7;
                                const nextOpens = opensCopy[nextDay] || [];

                                // Find any range in next day that starts exactly at "00:00"
                                for (let j = 0; j < nextOpens.length; j++) {
                                  if (nextOpens[j] === "00:00") {
                                    // Mark this next-day range to be skipped (it's a continuation)
                                    skipRanges[nextDay].add(j);
                                    break; // Only merge with the first 00:00 found
                                  }
                                }
                              }
                            }
                          }

                          // Second pass: Build the merged display, respecting skip markers
                          for (let day = 0; day < 7; day++) {
                            const opens = opensCopy[day] || [];
                            const closes = closesCopy[day] || [];

                            for (let i = 0; i < opens.length; i++) {
                              // Skip ranges that are continuations from previous day
                              if (skipRanges[day].has(i)) continue;

                              const open = opens[i];
                              const close = closes[i];
                              if (!open || !close) continue;

                              // If this range ends at midnight (24:00), try to merge with next day's 00:00
                              if (close === "24:00") {
                                const nextDay = (day + 1) % 7;
                                const nextOpens = opensCopy[nextDay] || [];
                                const nextCloses = closesCopy[nextDay] || [];

                                // Find the first 00:00 in next day (should be marked as skipped)
                                const zeroIndex = nextOpens.findIndex(x => x === "00:00");

                                if (zeroIndex !== -1 && nextCloses[zeroIndex]) {
                                  // Merge: show open (today) -> next day's close
                                  merged[day].push(`${formatTime(open)} – ${formatTime(nextCloses[zeroIndex])}`);
                                  continue; // we've handled this open-close via merge
                                }
                              }

                              // Normal same-day range
                              merged[day].push(`${formatTime(open)} – ${formatTime(close)}`);
                            }
                          }

                          // --- render ---
                          return days.map((day, index) => {
                            const ranges = merged[index];
                            const isClosed = ranges.length === 0;
                            // join multiple ranges compactly (will wrap if too long)
                            const display = ranges.join(" \u00B7 ");

                            return (
                              <Flex key={day} gap="2" style={{ alignItems: "center", alignSelf: "stretch", width: "100%",
                                justifyContent: "space-between"}}>
                                <Text size="2" weight="medium">{day}</Text>
                                <Flex direction="column" style={{ justifyContent: "flex-end" }}>
                                  {isClosed ? (
                                    <Badge color="red">Closed</Badge>
                                  ) : (
                                    <Badge color="green">{display}</Badge>
                                  )}
                                </Flex>
                              </Flex>
                            );
                          });
                        })()}
                      </Flex>
                    </Flex>
                    <Separator size="4" orientation="horizontal" />

                    {/* Brand section */}
                    <Flex gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                      <Flex direction="column" gap="2" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "25%" }}>
                        <Flex gap="2" align="center" style={{alignSelf: 'stretch'}}>
                          {isManager ? (
                            <LockClosedIcon width={12} height={12} />
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" 
                              strokeWidth={1.5} stroke="currentColor" className="size-4">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.098 19.902a3.75 3.75 0 0 0 5.304 0l6.401-6.402M6.75 21A3.75 3.75 0 0 1 3 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 0 0 3.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008Z" />
                            </svg>
                          )}
                          <Flex gap="1" align="center">
                          <Text size="3" weight="medium">Brand</Text>
                          {isManager && (
                            <Tooltip content="Only owners can assign locations to brands">
                              <InfoCircledIcon width={12} height={12} color="gray" />
                            </Tooltip>
                          )}
                        </Flex>
                        </Flex>
                        <Text size="1" color="gray">You can assign this location to a brand.</Text>
                      </Flex>
                      <Flex direction="column" gap="4" style={{ alignItems: "flex-end", alignSelf: "stretch", width: "75%", 
                        flex: "1 0 0" }}>
                        {/* Brand Select Field */}
                        <Flex direction="column" gap="1" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "100%" }}>
                          {(() => {
                            const isBrandLocked = location.brand_id && !brandsData?.find((b) => b.id === location.brand_id)?.is_default;
                            const currentBrand = brandsData?.find((b) => b.id === location.brand_id);
                            const isDefaultBrand = currentBrand?.is_default;

                            if (isBrandLocked && currentBrand) {
                              return (
                                <Tooltip content="This location's brand is locked. To use a different brand, create a new location or contact support.">
                                  <Flex style={{ width: "100%", position: "relative" }}>
                                    <TextField.Root style={{ width: "100%" }} value={currentBrand.name} readOnly disabled />
                                    <LockClosedIcon
                                      width="16"
                                      height="16"
                                      style={{
                                        position: "absolute",
                                        right: "12px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                      }}
                                    />
                                  </Flex>
                                </Tooltip>
                              );
                            }

                            if (isManager) {
                              // For managers, show read-only field with current brand
                              const displayText = currentBrand?.is_default ? 'No brand' : currentBrand?.name || 'No brand';
                              return (
                                <Tooltip content="Only owners can assign locations to brands">
                                  <TextField.Root 
                                    style={{ width: "100%" }} 
                                    value={displayText} 
                                    readOnly 
                                    disabled
                                  />
                                </Tooltip>
                              );
                            }

                            return (
                              <Select.Root
                                style={{ width: "100%" }}
                                value={
                                  modifiedLocations[location.id]?.brand_id !== undefined
                                    ? modifiedLocations[location.id].brand_id === null
                                      ? "" // Use empty string to show placeholder
                                      : String(modifiedLocations[location.id].brand_id)
                                    : location.brand_id && !isDefaultBrand
                                    ? String(location.brand_id)
                                    : "" // Use empty string to show placeholder
                                }
                                onValueChange={(value) => {
                                  handleBrandSelection(location.id, value);
                                }}
                              >
                                <Select.Trigger
                                  variant="surface"
                                  style={{
                                    width: "100%",
                                  }}
                                  placeholder="Select..."
                                />
                                <Select.Content position="popper">
                                  <Select.Group>
                                    <Select.Item value="_placeholder">
                                      None
                                    </Select.Item>
                                    {nonDefaultBrands.map((brand) => (
                                      <Select.Item key={brand.id} value={String(brand.id)}>
                                        {brand.name}
                                      </Select.Item>
                                    ))}
                                    {!isManager && (
                                      <Select.Item value="create_brand">
                                        <Flex gap="1" align="center">
                                          <Text>Create brand...</Text>
                                        </Flex>
                                      </Select.Item>
                                    )}
                                  </Select.Group>
                                </Select.Content>
                              </Select.Root>
                            );
                          })()}
                        </Flex>

                        {!isManager && (
                          <AlertDialog.Root>
                            <AlertDialog.Trigger asChild>
                              <Button
                                size="2"
                                variant="solid"
                                style={{ cursor: modifiedLocations[location.id]?.brand_id !== undefined ? "default" : "not-allowed" }}
                                disabled={modifiedLocations[location.id]?.brand_id === undefined || (location.brand_id && !brandsData?.find((b) => b.id === location.brand_id)?.is_default)}
                                loading={isLoadingBrand}>
                                Save changes
                              </Button>
                            </AlertDialog.Trigger>
                          <AlertDialog.Content>
                            <AlertDialog.Title>Confirm Brand Selection</AlertDialog.Title>
                            <AlertDialog.Description>
                              {/* Slot container */}
                              <Flex gap="4" direction="column">
                                {/* Lock content */}
                                <Flex gap="2" style={{ alignItems: 'flex-start', alignSelf: 'stretch'}}>
                                  <Flex p="1" gap="2" align="center">
                                    <LockClosedIcon width={12} height={12} />
                                  </Flex>
                                  <Text size="2">
                                    This location will be locked to the selected brand. You won't be able to change it later.
                                  </Text>
                                </Flex>
                                {/* Swatch content */}
                                <Flex gap="2" style={{ alignItems: 'flex-start', alignSelf: 'stretch'}}>
                                  <Flex p="1" gap="2" align="center">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" 
                                      strokeWidth={1.5} stroke="currentColor" className="size-3">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.098 19.902a3.75 3.75 0 0 0 5.304 0l6.401-6.402M6.75 21A3.75 3.75 0 0 1 3 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 0 0 3.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008Z" />
                                    </svg>
                                  </Flex>
                                  <Text size="2">
                                    This location will adopt the selected brand's captive portal design, network names, 
                                    passwords, and QR codes.
                                  </Text>
                                </Flex>
                                {/* Acceptance checkbox */}
                                <Flex p="4"
                                  style={{ border: '1px solid var(--gray-a3', borderRadius: 'var(--radius-2)',}}>
                                  <Text as="label" size="2">
                                    <Flex gap="2">
                                      <Checkbox size="2" variant="surface" 
                                        checked={isCheckboxChecked}
                                        onCheckedChange={(checked) => setIsCheckboxChecked(checked)} />
                                        I understand this location will be permanently locked to this brand and adopt its 
                                        captive portal, network names, passwords, and QR codes.
                                    </Flex>
                                  </Text>
                                </Flex>
                              </Flex>
                            </AlertDialog.Description>

                            <Flex gap="3" justify="end" mt="4">
                              <AlertDialog.Cancel>
                                <Button variant="soft" color="gray">
                                  Cancel
                                </Button>
                              </AlertDialog.Cancel>
                              <AlertDialog.Action>
                                <Button variant="solid"
                                  onClick={() => handleSaveBrandChanges(location.id)}
                                  disabled={!isCheckboxChecked}>
                                  Save changes
                                </Button>
                              </AlertDialog.Action>
                            </Flex>
                          </AlertDialog.Content>
                          </AlertDialog.Root>
                        )}
                      </Flex>
                    </Flex>
                    <Separator size="4" orientation="horizontal" />

                    {/* Timezone Selector */}
                    <Flex gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                      <Flex direction="column" gap="2" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "25%" }}>
                        <Flex gap="2" align="center" style={{alignSelf: 'stretch'}}>
                          <GlobeIcon width={14} height={14} />
                          <TruncatedText size="3" weight="medium">Location timezone</TruncatedText>
                        </Flex>
                        <Text size="1" color="gray">You can manage the timezone for this location.</Text>
                      </Flex>
                      <Flex direction="column" gap="4" style={{ alignItems: "flex-end", alignSelf: "stretch", width: "60%", flex: "1 0 0" }}>
                        {/* Timezone Select Field */}
                        <Flex direction="column" gap="1" style={{ alignItems: "center", alignSelf: "stretch", width: "100%" }}>
                          <Select.Root style={{ width: "100%" }}
                            value={modifiedLocations[location.id]?.timezone ?? location.timezone ?? getUserTimezone()}
                            onValueChange={(value) => handleFieldChange(location.id, "timezone", value)}>
                            <Select.Trigger variant="surface" style={{ width: "100%" }} />
                            <Select.Content position="popper">
                              <Select.Group>
                                {timezoneOptions?.map((timezone) => (
                                  <Select.Item key={timezone.value} value={timezone.value}>
                                    {timezone.label}
                                  </Select.Item>
                                ))}
                              </Select.Group>
                            </Select.Content>
                          </Select.Root>
                        </Flex>
                        <Button
                          size="2"
                          variant="solid"
                          style={{ cursor: modifiedLocations[location.id]?.timezone && 
                            (modifiedLocations[location.id]?.timezone !== (location.timezone ?? getUserTimezone())) 
                            ? "default" : "not-allowed" }}
                          disabled={!modifiedLocations[location.id]?.timezone || 
                            (modifiedLocations[location.id]?.timezone === (location.timezone ?? getUserTimezone()))}
                          onClick={() => handleSaveChanges(location.id)}>
                          Save changes
                        </Button>
                      </Flex>
                    </Flex>

                    {/* Captive Portal Behaviour Section - Only show for locations with network integrations */}
                    {hasNetworkIntegration(location) && (
                      <>
                        <Separator size="4" orientation="horizontal" style={{ minHeight: '1px'}} />
                        
                        <Flex gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Flex direction="column" gap="2" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "25%" }}>
                            <Flex gap="2" align="center" style={{alignSelf: 'stretch'}}>
                              <LapTimerIcon width={14} height={14} />
                              <TruncatedText size="3" weight="medium">Captive portal behaviour</TruncatedText>
                            </Flex>
                            <Text size="1" color="gray">You can manage the guest portal authorisation duration.</Text>
                          </Flex>
                          <Flex direction="column" gap="4" style={{ alignItems: "flex-end", alignSelf: "stretch", width: "60%", flex: "1 0 0" }}>
                            {/* Guest Authentication Expiry Select Field */}
                            <Flex direction="column" gap="1" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "100%" }}>
                               <Flex gap="1" align="center" style={{ width: "100%" }}>
                              <Text size="2" weight="medium" style={{ width: "100%" }}>
                                Guest authentication expiry
                              </Text>
                              <Tooltip content="Choose how long a guest stays authenticated before they're asked to submit the captive portal again.">
                                <InfoCircledIcon width={14} height={14} />
                              </Tooltip>
                              </Flex>
                              <Select.Root
                                style={{ width: "100%" }}
                                value={String(modifiedLocations[location.id]?.splash_timeout ?? getCurrentSplashTimeout(location))}
                                onValueChange={(value) => handleSplashTimeoutSelect(location.id, value)}
                              >
                                <Select.Trigger variant="surface" style={{ width: "100%" }} />
                                <Select.Content position="popper" style={{ maxHeight: '300px'}}>
                                  <Select.Group>
                                    {timeoutOptions.map((option) => (
                                      <Select.Item key={option.value} value={String(option.value)}>
                                        {option.label}
                                        {option.value === getCurrentSplashTimeout(location)}
                                      </Select.Item>
                                    ))}
                                  </Select.Group>
                                </Select.Content>
                              </Select.Root>
                            </Flex>
                            {/* Save changes for Guest Authentication Expiry (right-aligned) */}
                            <Dialog.Root>
                              <Dialog.Trigger>
                                <Button
                                  size="2"
                                  variant="solid"
                                  style={{ alignSelf: "flex-end", cursor: modifiedLocations[location.id]?.splash_timeout !== undefined && (modifiedLocations[location.id]?.splash_timeout !== getCurrentSplashTimeout(location)) ? "default" : "not-allowed" }}
                                  disabled={
                                    modifiedLocations[location.id]?.splash_timeout === undefined ||
                                    modifiedLocations[location.id]?.splash_timeout === getCurrentSplashTimeout(location)
                                  }
                                >
                                  Save changes
                                </Button>
                              </Dialog.Trigger>
                              <Dialog.Content width="1280px">
                                <Dialog.Title>Adjust Captive Portal Behaviour</Dialog.Title>
                                <Dialog.Description size="2" mb="4">
                                  Before you update the <Text size="2" weight="bold">guest authentication expiry</Text>, keep in mind:
                                </Dialog.Description>
                                <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start" gap="5">
                                  <Text style={{ padding: "0px var(--space-5)" }} size="1" weight="regular">
                                    {usesPerGuestTPLinkTimeout(location) ? (
                                      <ul style={{ listStyleType: "disc", alignItems: "center" }}>
                                        <li>The new duration applies when a guest is next authorised through the captive portal.</li>
                                        <li>Existing authorised guest sessions keep their current expiry.</li>
                                        <li>No access point restart is required.</li>
                                      </ul>
                                    ) : (
                                      <ul style={{ listStyleType: "disc", alignItems: "center" }}>
                                        <li>Access points will perform a soft restart to apply the new setting.</li>
                                        <li>Connected devices may briefly lose their connection.</li>
                                        <li>Changes take effect immediately.</li>
                                      </ul>
                                    )}
                                  </Text>
                                  <Flex
                                    className="checkbox-container"
                                    style={{ borderRadius: "var(--space-1)", border: "1px solid var(--gray-a3)", background: "var(--color-panel-solid)" }}
                                    display="flex"
                                    width="552px"
                                    p="4"
                                    align="start"
                                    gap="2"
                                  >
                                    <Text as="label" size="2" weight="regular">
                                      <Flex as="span" gap="2">
                                        <Checkbox
                                          checked={isExpiryCheckboxChecked}
                                          onCheckedChange={(checked) => setIsExpiryCheckboxChecked(checked)}
                                          size="2"
                                          variant="surface"
                                        />
                                        {usesPerGuestTPLinkTimeout(location)
                                          ? "I understand that this duration applies to future guest authorisations and does not alter existing sessions."
                                          : "I understand that changing the splash page timeout will cause a soft restart of the access points hosting the guest Wi-Fi network."}
                                      </Flex>
                                    </Text>
                                  </Flex>
                                </Flex>
                                <Flex gap="3" mt="4" justify="end">
                                  <Dialog.Close
                                    onClick={() => {
                                      setIsExpiryCheckboxChecked(false);
                                    }}
                                  >
                                    <Button variant="soft" color="gray">
                                      Cancel
                                    </Button>
                                  </Dialog.Close>
                                  <Dialog.Close
                                    disabled={!isExpiryCheckboxChecked}
                                    onClick={() => {
                                      handleSaveSplashTimeoutChanges(location.id);
                                      setIsExpiryCheckboxChecked(false);
                                    }}
                                  >
                                    <Button variant="solid" loading={isSavingExpiry}>Save changes</Button>
                                  </Dialog.Close>
                                </Flex>
                              </Dialog.Content>
                            </Dialog.Root>
                          </Flex>
                        </Flex>
                      </>
                    )}
                  </Flex>
                )}
              </Card>
            ))}
        </Flex>
      </Flex>

      <Dialog.Root open={createBrandModalOpen} onOpenChange={setCreateBrandModalOpen}>
        <Dialog.Content maxWidth="400px">
          <Dialog.Title>Create Brand</Dialog.Title>
          <Flex direction="column" gap="3">
            <TextField.Root value={newBrandName} onChange={(e) => setNewBrandName(e.target.value)} placeholder="New brand name" style={{ width: "100%" }} />
          </Flex>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleCreateBrand} loading={isCreatingBrand} disabled={!newBrandName.trim()}>
              Create
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <ToastAlert />
    </React.Fragment>
  );
};

export default OrganisationLocations;
